import { useCallback, useContext } from "react";

import {
    AnalyticsContextValue,
    DEFAULT_FREQUENCY_CAP_VALUE,
    LocalStorageDataForFrequency,
} from "@swiggy-private/checkout-core";

import {
    clickFtueCrouton,
    impressionCallout,
    impressionFtueCrouton,
    impressionFtueNewTag,
    impressionFtueAnimation,
} from "@cart-client/analytics/events";
import { CartContext } from "@cart-client/contexts/CartContext";
import { getCartId } from "@cart-client/helpers/Cart";
import { LOCAL_STORAGE_KEYS, LocalStorage } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

export function useToingDeliveryModesAnalytics() {
    const {
        cartState: { reviewedCart: cartResponse },
    } = useContext(CartContext);

    const cartId = getCartId(cartResponse);

    const impressionDeliverySpeedCallout = useCallback(
        (impressionEvent: AnalyticsContextValue["impressionEvent"], text: string): void => {
            impressionCallout(impressionEvent, JSON.stringify({ cartId }), JSON.stringify({ text }));
        },
        [cartId],
    );

    const impressionFtueDeliveryModeNewTag = useCallback(
        (impressionEvent: AnalyticsContextValue["impressionEvent"]): void => {
            const freqData = LocalStorage.getItem(
                LOCAL_STORAGE_KEYS.DELIVERY_SPEED_NEW_TAG_FREQUENCY_CAPPING_KEY,
                DEFAULT_FREQUENCY_CAP_VALUE,
            ) as LocalStorageDataForFrequency;
            const storedCount = freqData?.counterValue ?? 0;
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const contextPayload = JSON.stringify({ session_count: storedCount });
            impressionFtueNewTag(impressionEvent, contextPayload, JSON.stringify({ cartId }));
        },
        [cartId],
    );

    const impressionToingDeliverySpeedCroutonShown = useCallback(
        (impressionEvent: AnalyticsContextValue["impressionEvent"], croutonText: string): void => {
            const freqData = LocalStorage.getItem(
                LOCAL_STORAGE_KEYS.DELIVERY_SPEED_CROUTON_FREQUENCY_CAPPING_KEY,
                DEFAULT_FREQUENCY_CAP_VALUE,
            ) as LocalStorageDataForFrequency;
            const storedCount = freqData?.counterValue ?? 0;
            impressionFtueCrouton(
                impressionEvent,
                JSON.stringify({ message: croutonText }),
                // eslint-disable-next-line @typescript-eslint/naming-convention
                JSON.stringify({ cart_id: cartId, session_count: storedCount }),
            );
        },
        [cartId],
    );

    const impressionFtueDeliveryModeAnimation = useCallback(
        (impressionEvent: AnalyticsContextValue["impressionEvent"]): void => {
            const originalCartId = cartResponse?.data?.originalCartId;
            const freqData = LocalStorage.getItem(
                LOCAL_STORAGE_KEYS.DELIVERY_MODE_ANIMATION_FREQUENCY_CAPPING_KEY,
                DEFAULT_FREQUENCY_CAP_VALUE,
            ) as LocalStorageDataForFrequency;
            const storedCount = freqData?.counterValue ?? 0;
            impressionFtueAnimation(
                impressionEvent,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                JSON.stringify({ session_count: storedCount }),
                JSON.stringify({ cartId, originalCartId }),
            );
        },
        [cartId, cartResponse],
    );

    const clickToingDeliverySpeedFtueCrouton = useCallback(
        (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
            if (isEmpty(cartId)) {
                return;
            }
            const freqData = LocalStorage.getItem(
                LOCAL_STORAGE_KEYS.DELIVERY_SPEED_CROUTON_FREQUENCY_CAPPING_KEY,
                DEFAULT_FREQUENCY_CAP_VALUE,
            ) as LocalStorageDataForFrequency;
            const storedCount = freqData?.counterValue ?? 0;
            clickFtueCrouton(
                clickEvent,
                JSON.stringify({ cartId }),
                // eslint-disable-next-line @typescript-eslint/naming-convention
                JSON.stringify({ cart_id: cartId, session_count: storedCount, entry_point: "FTUE" }),
            );
        },
        [cartId],
    );

    return {
        impressionDeliverySpeedCallout,
        impressionFtueDeliveryModeNewTag,
        impressionToingDeliverySpeedCroutonShown,
        clickToingDeliverySpeedFtueCrouton,
        impressionFtueDeliveryModeAnimation,
    };
}
